import React from 'react';
import { Label, NumberInput, Switch } from '@/components/ui';
import type { MealBreakConfig, Role } from '@/types';

import { CustomizedFieldIndicator } from '../../../form/CustomizedFieldIndicator';
import { AnimatedFormError } from '../../../form/AnimatedFormError';
import { ClickableDescription } from '../components/ClickableDescription';
import { fieldLabelClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { allApplicableRolesAreDayRate, hasAnyDayRateRoles } from '../../shared/utils';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';

interface Props {
  context: 'user' | 'client';
  mealConfigCustomized: Partial<Record<keyof MealBreakConfig, boolean>>;
  safeConfig: MealBreakConfig;
  onMealConfigChange: (updates: Partial<MealBreakConfig>) => void;
  anyHalfDayRoles: boolean;
  roles?: Role[];
  minimumBillableHours?: number | null;
  halfDayHours?: number | null;
  rolesRow?: React.ReactNode;
  halfDayBreaksProps?: Record<string, unknown>;
  halfDayBreaksError?: React.ReactNode;
}

export const CountModeSettingsSection: React.FC<Props> = ({
  context,
  mealConfigCustomized,
  safeConfig,
  onMealConfigChange,
  anyHalfDayRoles,
  roles = [],
  minimumBillableHours,
  halfDayHours,
  rolesRow,
  halfDayBreaksProps,
  halfDayBreaksError,
}) => {
  const allDayRate = React.useMemo(
    () => allApplicableRolesAreDayRate(roles, safeConfig.applicableRoleIds),
    [roles, safeConfig.applicableRoleIds]
  );
  const anyDayRate = hasAnyDayRateRoles(roles, safeConfig.applicableRoleIds);
  const thresholdFromBilling = allDayRate && minimumBillableHours != null ? minimumBillableHours : null;
  const hasMixedBilling = !allDayRate && anyDayRate;

  const scalingEnabled = safeConfig.additionalBreakIntervalHours != null;
  const showHalfDayBreaks = anyHalfDayRoles && halfDayHours != null && halfDayHours > 0;
  const showDayRateConditions = showHalfDayBreaks || (scalingEnabled && hasMixedBilling && minimumBillableHours != null);
  const showHourlyConditions = thresholdFromBilling == null && scalingEnabled;
  const showAnyConditions = showDayRateConditions || showHourlyConditions;
  const showRoleDividers = (roles?.length ?? 0) > 1;


  const handleScalingToggle = (checked: boolean) => {
    if (checked) {
      const updates: Partial<MealBreakConfig> = { additionalBreakIntervalHours: 4 };
      // For non-day-rate, set a default threshold if not already set
      if (thresholdFromBilling == null && safeConfig.thresholdHours == null) {
        updates.thresholdHours = 10;
      }
      onMealConfigChange(updates);
    } else {
      onMealConfigChange({ additionalBreakIntervalHours: null });
    }
  };

  const requiredBreaksDescription = scalingEnabled
    ? allDayRate
      ? 'How many qualifying meal breaks are required before reaching the minimum billable hours.'
      : 'How many qualifying meal breaks are required before reaching the required break threshold below.'
    : 'How many total qualifying meal breaks are required per shift, with no deadline and regardless of shift length.';

  // Track toggle separately so clearing the input doesn't deactivate the toggle
  const [halfDayBreaksEnabled, setHalfDayBreaksEnabled] = React.useState(
    safeConfig.requiredBreaksHalfDay != null
  );

  // Sync toggle state when config changes externally (e.g. revert, load)
  React.useEffect(() => {
    if (safeConfig.requiredBreaksHalfDay != null && !halfDayBreaksEnabled) {
      setHalfDayBreaksEnabled(true);
    }
  }, [safeConfig.requiredBreaksHalfDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHalfDayToggle = (checked: boolean) => {
    setHalfDayBreaksEnabled(checked);
    if (checked) {
      onMealConfigChange({
        requiredBreaksHalfDay: safeConfig.requiredBreaks ?? 1,
        applyOnHalfDays: true,
      });
    } else {
      onMealConfigChange({
        requiredBreaksHalfDay: null,
        applyOnHalfDays: true,
      });
    }
  };

  const halfDayBreaksField = (
    <div>
      <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="half-day-breaks-toggle" className={fieldLabelClass}>Half-Day Breaks</Label>
            <CustomizedFieldIndicator isCustomized={mealConfigCustomized.requiredBreaksHalfDay} context={context} />
          </div>
          <div className="hidden md:block">
            <ClickableDescription>
              Require meal breaks on half-day shifts. When off, half-day shifts do not trigger meal break surcharges.
            </ClickableDescription>
          </div>
        </div>
        <div className="flex justify-end md:justify-end md:w-full">
          <Switch
            id="half-day-breaks-toggle"
            checked={halfDayBreaksEnabled}
            onCheckedChange={handleHalfDayToggle}
          />
        </div>
        <div className="col-span-2 md:hidden">
          <ClickableDescription>
            Require meal breaks on half-day shifts. When off, half-day shifts do not trigger meal break surcharges.
          </ClickableDescription>
        </div>
      </div>
      <AnimatedCollapse open={halfDayBreaksEnabled}>
        <div className="mt-4 pl-4 border-l-2 border-muted">
          <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="required-breaks-half-day" className={fieldLabelClass}>Required Breaks (Half-Day)</Label>
              </div>
              <div className="hidden md:block">
                <ClickableDescription>
                  How many qualifying meal breaks are required on half-day shifts.
                </ClickableDescription>
              </div>
            </div>
            <div className="flex flex-col items-end md:items-end md:w-full">
              <NumberInput
                id="required-breaks-half-day"
                size="xs"
                minValue={1}
                maxValue={5}
                step={1}
                {...(halfDayBreaksProps || {
                  value: safeConfig.requiredBreaksHalfDay ?? '',
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => onMealConfigChange({ requiredBreaksHalfDay: e.target.value === '' ? null : Number(e.target.value) }),
                })}
              />
              <AnimatedFormError className="w-full text-left md:text-right mt-1">
                {halfDayBreaksError}
              </AnimatedFormError>
            </div>
            <div className="col-span-2 md:hidden">
              <ClickableDescription>
                How many qualifying meal breaks are required on half-day shifts.
              </ClickableDescription>
            </div>
          </div>
        </div>
      </AnimatedCollapse>
    </div>
  );

  const thresholdDescription = hasMixedBilling
    ? 'Hourly role shifts at or under this length require the breaks above. Longer shifts require additional breaks.'
    : 'Shifts at or under this length require the number of breaks above. Longer shifts require additional breaks.';
  const thresholdField = (
    <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-1.5">
          <Label htmlFor="base-threshold-hours" className={fieldLabelClass}>Required Break Threshold</Label>
          <CustomizedFieldIndicator isCustomized={mealConfigCustomized.thresholdHours} context={context} />
        </div>
        <div className="hidden md:block">
          <ClickableDescription>
            {thresholdDescription}
          </ClickableDescription>
        </div>
      </div>
      <div className="flex flex-col items-end md:items-end md:w-full">
        <NumberInput
          id="base-threshold-hours"
          unit="hrs"
          size="xs"
          value={safeConfig.thresholdHours ?? ''}
          onChange={(e) => onMealConfigChange({ thresholdHours: e.target.value === '' ? null : Number(e.target.value) })}
          minValue={1}
          maxValue={24}
          step={0.5}
        />
      </div>
      <div className="col-span-2 md:hidden">
        <ClickableDescription>
          {thresholdDescription}
        </ClickableDescription>
      </div>
    </div>
  );

  return (
    <div>
      {/* ── Shared fields ── */}

      {/* Required Breaks */}
      <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="required-breaks-in-base" className={fieldLabelClass}>Meal Breaks Required</Label>
            <CustomizedFieldIndicator isCustomized={mealConfigCustomized.requiredBreaks} context={context} />
          </div>
          <div className="hidden md:block">
            <ClickableDescription>
              {requiredBreaksDescription}
            </ClickableDescription>
          </div>
        </div>
        <div className="flex flex-col items-end md:items-end md:w-full">
          <NumberInput
            id="required-breaks-in-base"
            size="xs"
            value={safeConfig.requiredBreaks ?? ''}
            onChange={(e) => onMealConfigChange({ requiredBreaks: e.target.value === '' ? null : Number(e.target.value) })}
            minValue={1}
            maxValue={10}
            step={1}
          />
        </div>
        <div className="col-span-2 md:hidden">
          <ClickableDescription>
            {requiredBreaksDescription}
          </ClickableDescription>
        </div>
      </div>

      {/* Scale toggle + Additional Break */}
      <div className="mt-4">
        <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="scale-with-length" className={fieldLabelClass}>Scale with Shift Length</Label>
            </div>
            <div className="hidden md:block">
              <ClickableDescription>
                Require additional breaks as shift length increases.
              </ClickableDescription>
            </div>
          </div>
          <div className="flex justify-end md:justify-end md:w-full">
            <Switch
              id="scale-with-length"
              checked={scalingEnabled}
              onCheckedChange={handleScalingToggle}
            />
          </div>
          <div className="col-span-2 md:hidden">
            <ClickableDescription>
              Require additional breaks as shift length increases.
            </ClickableDescription>
          </div>
        </div>

        <AnimatedCollapse open={scalingEnabled}>
          <div className="mt-4">
            <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="additional-break-interval" className={fieldLabelClass}>Additional Break Every</Label>
                  <CustomizedFieldIndicator isCustomized={mealConfigCustomized.additionalBreakIntervalHours} context={context} />
                </div>
                <div className="hidden md:block">
                  <ClickableDescription>
                    Beyond the required break threshold, an additional break is required per this many additional hours.
                  </ClickableDescription>
                </div>
              </div>
              <div className="flex flex-col items-end md:items-end md:w-full">
                <NumberInput
                  id="additional-break-interval"
                  unit="hrs"
                  size="xs"
                  value={safeConfig.additionalBreakIntervalHours ?? ''}
                  onChange={(e) => onMealConfigChange({ additionalBreakIntervalHours: e.target.value === '' ? null : Number(e.target.value) })}
                  minValue={1}
                  maxValue={12}
                  step={0.5}
                />
              </div>
              <div className="col-span-2 md:hidden">
                <ClickableDescription>
                  Beyond the required break threshold, an additional break is required per this many additional hours.
                </ClickableDescription>
              </div>
            </div>
          </div>
        </AnimatedCollapse>
      </div>

      {/* ── Roles row ── */}
      {rolesRow}

      {/* ── Condition sections ── */}
      <AnimatedCollapse open={showAnyConditions}>
        <div className="mt-4">
          <AnimatedCollapse open={showDayRateConditions}>
            <div role="group" aria-label="Day Rate Role Conditions">
              {showRoleDividers && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 border-t border-dashed border-border/40" />
                  <span className="font-label text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.12em] text-muted-foreground shrink-0">Day Rate Roles</span>
                  <div className="flex-1 border-t border-dashed border-border/40" />
                </div>
              )}
              <div>
                <AnimatedCollapse open={showHalfDayBreaks}>
                  {halfDayBreaksField}
                </AnimatedCollapse>
                <AnimatedCollapse open={scalingEnabled && hasMixedBilling && minimumBillableHours != null}>
                  <div className="mt-4 w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Label className={fieldLabelClass}>Required Break Threshold</Label>
                      </div>
                      <div className="hidden md:block">
                        <ClickableDescription>
                          Day rate role shifts at or under the minimum billable hours require the breaks above. Longer shifts require additional breaks.
                        </ClickableDescription>
                      </div>
                    </div>
                    <div className="flex flex-col items-end md:items-end md:w-full">
                      <NumberInput
                        size="xs"
                        unit="hrs"
                        value={minimumBillableHours ?? ''}
                        disabled
                      />
                    </div>
                    <div className="col-span-2 md:hidden">
                      <ClickableDescription>
                        Day rate role shifts at or under the minimum billable hours require the breaks above. Longer shifts require additional breaks.
                      </ClickableDescription>
                    </div>
                  </div>
                </AnimatedCollapse>
              </div>
            </div>
          </AnimatedCollapse>

          <AnimatedCollapse open={showHourlyConditions}>
            <div className="mt-2" role="group" aria-label="Hourly Role Conditions">
              {showRoleDividers && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 border-t border-dashed border-border/40" />
                  <span className="font-label text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.12em] text-muted-foreground shrink-0">Hourly Roles</span>
                  <div className="flex-1 border-t border-dashed border-border/40" />
                </div>
              )}
              {thresholdField}
            </div>
          </AnimatedCollapse>
        </div>
      </AnimatedCollapse>
    </div>
  );
};
